import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Pagination,
} from '@mui/material';
import {
  LocalPharmacy,
  Search,
  Close,
  Info,
  LocalHospital,
  MonetizationOn,
  Category,
  Warning,
  LocalShipping,
  Business,
  CalendarToday,
  Receipt,
  PersonOff,
  Person,
  Phone,
  AttachMoney,
  Download,
  Settings,
  Description,
  AccountBalance,
  AccountBalanceWallet,
  SwapHoriz,
  TrendingDown,
  Savings,
  EventRepeat,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Store } from '../types';
import MinMaxManagement from './MinMaxManagement';
import CustomerRefillsManagement from './CustomerRefillsManagement';
import PharmacistAudit from './PharmacistAudit';
import PurchaseReconciliation from './PurchaseReconciliation';
import PurchasePayments from './PurchasePayments';
import EvitalrxSales from './EvitalrxSales';
import EvitalrxPurchaseReturns from './EvitalrxPurchaseReturns';
import StockTransfers from './StockTransfers';
import NonMovingItems from './NonMovingItems';
import DiscountOptimizer from './DiscountOptimizer';
import MonthlyRefills from './MonthlyRefills';

interface IndianMedicine {
  id: number;
  name: string;
  price: string;
  Is_discontinued: string;
  manufacturer_name: string;
  type: string;
  pack_size_label: string;
  short_composition1: string;
  short_composition2: string;
  salt_composition: string;
  medicine_desc: string;
  side_effects: string;
  drug_interactions: string;
}

interface DistributorLookup {
  distributor_id: number;
  distributor_name: string;
  last_purchase_date: string;
  medicine_name?: string;
  manufacturer_name?: string;
  total_purchases: number;
  last_bill_no?: string;
}

interface ChurnedCustomer {
  customer_id: number;
  customer_name: string;
  customer_number: string;
  last_purchase_date: string;
  total_sales_amount: number;
  total_bills: number;
  days_since_last_purchase: number;
}

interface ChurnedCustomersResponse {
  customers: ChurnedCustomer[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface UtilitiesProps {
  selectedStore: Store | null;
}

const Utilities: React.FC<UtilitiesProps> = ({ selectedStore }) => {
  const { dbUser } = useAuth();
  
  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    if (!dbUser?.permission) return false;
    const userPermissions = dbUser.permission.split(',').map((p: string) => p.trim());
    return userPermissions.includes(permission);
  };

  const [medicineDialogOpen, setMedicineDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<IndianMedicine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMedicine, setSelectedMedicine] = useState<IndianMedicine | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Distributor Lookup States
  const [distributorDialogOpen, setDistributorDialogOpen] = useState(false);
  const [distributorSearchQuery, setDistributorSearchQuery] = useState('');
  const [distributorResults, setDistributorResults] = useState<DistributorLookup[]>([]);
  const [distributorLoading, setDistributorLoading] = useState(false);
  const [distributorError, setDistributorError] = useState<string | null>(null);

  // Churned Customer States
  const [churnedDialogOpen, setChurnedDialogOpen] = useState(false);
  const [churnedResults, setChurnedResults] = useState<ChurnedCustomer[]>([]);
  const [churnedLoading, setChurnedLoading] = useState(false);
  const [churnedError, setChurnedError] = useState<string | null>(null);
  
  // Min-Max Management States
  const [minMaxDialogOpen, setMinMaxDialogOpen] = useState(false);
  
  // Customer Refills Management States
  const [refillsManagementDialogOpen, setRefillsManagementDialogOpen] = useState(false);

  // Pharmacist Audit States
  const [pharmacistAuditDialogOpen, setPharmacistAuditDialogOpen] = useState(false);

  // Purchase Reconciliation States
  const [reconciliationDialogOpen, setReconciliationDialogOpen] = useState(false);

  // Purchase Payments States
  const [purchasePaymentsOpen, setPurchasePaymentsOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [purchaseReturnsOpen, setPurchaseReturnsOpen] = useState(false);

  // Inter-Store Stock Balancing States
  const [stockTransfersOpen, setStockTransfersOpen] = useState(false);

  // Non-Moving Items States
  const [nonMovingItemsOpen, setNonMovingItemsOpen] = useState(false);

  // Discount Optimizer States
  const [discountOptimizerOpen, setDiscountOptimizerOpen] = useState(false);

  // Monthly Customer Refills States
  const [monthlyRefillsOpen, setMonthlyRefillsOpen] = useState(false);
  
  const [churnedPage, setChurnedPage] = useState(1);
  const [churnedPagination, setChurnedPagination] = useState({
    total: 0,
    limit: 25,
    offset: 0,
    hasMore: false
  });
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const handleOpenMedicineDialog = () => {
    setMedicineDialogOpen(true);
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  };

  const handleCloseMedicineDialog = () => {
    setMedicineDialogOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  };

  const handleSearchMedicines = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/utilities/indian-medicines/search`, {
        params: { q: searchQuery.trim() }
      });
      
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        setError('No medicines found matching your search criteria');
      }
    } catch (error: any) {
      console.error('Error searching medicines:', error);
      setError('Failed to search medicines. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (medicine: IndianMedicine) => {
    setSelectedMedicine(medicine);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedMedicine(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchMedicines();
    }
  };

  // Distributor Lookup Handlers
  const handleOpenDistributorDialog = () => {
    setDistributorDialogOpen(true);
    setDistributorSearchQuery('');
    setDistributorResults([]);
    setDistributorError(null);
  };

  const handleCloseDistributorDialog = () => {
    setDistributorDialogOpen(false);
    setDistributorSearchQuery('');
    setDistributorResults([]);
    setDistributorError(null);
  };

  const handleSearchDistributors = async () => {
    if (!distributorSearchQuery.trim()) {
      setDistributorError('Please enter a medicine name or manufacturer name');
      return;
    }

    setDistributorLoading(true);
    setDistributorError(null);

    try {
      const response = await api.get(`/utilities/distributor-lookup`, {
        params: { q: distributorSearchQuery.trim() }
      });
      
      setDistributorResults(response.data);
      
      if (response.data.length === 0) {
        setDistributorError('No distributors found for this medicine/manufacturer');
      }
    } catch (error: any) {
      console.error('Error searching distributors:', error);
      setDistributorError('Failed to search distributors. Please try again.');
    } finally {
      setDistributorLoading(false);
    }
  };

  const handleDistributorKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchDistributors();
    }
  };

  // Churned Customer Handlers
  const handleOpenChurnedDialog = () => {
    setChurnedDialogOpen(true);
    setChurnedResults([]);
    setChurnedError(null);
    setChurnedPage(1);
    // Auto-load churned customers when dialog opens
    handleLoadChurnedCustomers(1);
  };

  const handleCloseChurnedDialog = () => {
    setChurnedDialogOpen(false);
    setChurnedResults([]);
    setChurnedError(null);
    setChurnedPage(1);
  };

  // Min-Max Management Handlers
  const handleOpenMinMaxDialog = () => {
    setMinMaxDialogOpen(true);
  };

  const handleCloseMinMaxDialog = () => {
    setMinMaxDialogOpen(false);
  };

  // Customer Refills Management Handlers
  const handleOpenRefillsManagementDialog = () => {
    setRefillsManagementDialogOpen(true);
  };

  const handleCloseRefillsManagementDialog = () => {
    setRefillsManagementDialogOpen(false);
  };

  // Pharmacist Audit Handlers
  const handleOpenPharmacistAuditDialog = () => {
    setPharmacistAuditDialogOpen(true);
  };

  const handleClosePharmacistAuditDialog = () => {
    setPharmacistAuditDialogOpen(false);
  };

  const handleLoadChurnedCustomers = async (page: number = churnedPage) => {
    setChurnedLoading(true);
    setChurnedError(null);

    try {
      // Get selected store from localStorage (same as the app uses)
      const savedStore = localStorage.getItem('selectedStore');
      let storeId = null;
      
      if (savedStore) {
        try {
          const store = JSON.parse(savedStore);
          storeId = store.id;
        } catch (error) {
          console.error('Error parsing saved store:', error);
        }
      }

      if (!storeId) {
        setChurnedError('Please select a store first from the top menu');
        return;
      }

      const offset = (page - 1) * churnedPagination.limit;

      const response = await api.get(`/utilities/churned-customers`, {
        params: { 
          store_id: storeId, 
          days: 70, 
          limit: churnedPagination.limit,
          offset: offset
        }
      });
      
      const data: ChurnedCustomersResponse = response.data;
      
      setChurnedResults(data.customers);
      setChurnedPagination(data.pagination);
      setChurnedPage(page);
      
      if (data.customers.length === 0 && page === 1) {
        setChurnedError('No churned customers found for the selected store');
      }
    } catch (error: any) {
      console.error('Error loading churned customers:', error);
      setChurnedError('Failed to load churned customers. Please try again.');
    } finally {
      setChurnedLoading(false);
    }
  };

  const handleChurnedPageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    handleLoadChurnedCustomers(page);
  };

  const handleDownloadChurnedCsv = async () => {
    setDownloadingCsv(true);
    
    try {
      // Get selected store from localStorage
      const savedStore = localStorage.getItem('selectedStore');
      let storeId = null;
      
      if (savedStore) {
        try {
          const store = JSON.parse(savedStore);
          storeId = store.id;
        } catch (error) {
          console.error('Error parsing saved store:', error);
        }
      }

      if (!storeId) {
        setChurnedError('Please select a store first from the top menu');
        return;
      }

      const response = await api.get(`/utilities/churned-customers`, {
        params: { 
          store_id: storeId, 
          days: 70, 
          download: true
        }
      });
      
      const data: ChurnedCustomersResponse = response.data;
      
      // Convert to CSV
      const csvContent = convertChurnedCustomersToCsv(data.customers);
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `churned-customers-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      setChurnedError('Failed to download CSV. Please try again.');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const convertChurnedCustomersToCsv = (customers: ChurnedCustomer[]): string => {
    const headers = ['Customer Name', 'Phone Number', 'Last Purchase Date', 'Days Inactive', 'Total Bills', 'Total Sales Amount'];
    const csvRows = [headers.join(',')];
    
    customers.forEach(customer => {
      const row = [
        `"${customer.customer_name || 'Unknown Customer'}"`,
        `"${customer.customer_number || ''}"`,
        `"${new Date(customer.last_purchase_date).toLocaleDateString()}"`,
        customer.days_since_last_purchase.toString(),
        customer.total_bills.toString(),
        customer.total_sales_amount.toString()
      ];
      csvRows.push(row.join(','));
    });
    
    return csvRows.join('\n');
  };

  const formatPrice = (price: string) => {
    return price ? `₹${price}` : 'N/A';
  };

  const getDiscontinuedChip = (isDiscontinued: string) => {
    const discontinued = isDiscontinued?.toLowerCase() === 'true' || isDiscontinued?.toLowerCase() === 'yes';
    return (
      <Chip
        label={discontinued ? 'Discontinued' : 'Available'}
        color={discontinued ? 'error' : 'success'}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalPharmacy />
        Utilities
      </Typography>
      
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Access utility tools and search capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalHospital color="primary" />
                <Typography variant="h6">
                  Indian Medicine Lookup
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Search and view detailed information about Indian medicines including composition, 
                pricing, side effects, and drug interactions.
              </Typography>
              
              <Button 
                variant="contained" 
                fullWidth 
                onClick={handleOpenMedicineDialog}
                startIcon={<Search />}
              >
                Search Medicines
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalShipping color="secondary" />
                <Typography variant="h6">
                  Distributor Lookup
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Find distributors for specific medicines or manufacturers based on purchase history. 
                See last purchase dates and contact information.
              </Typography>
              
              <Button 
                variant="contained" 
                fullWidth 
                onClick={handleOpenDistributorDialog}
                startIcon={<Search />}
                color="secondary"
              >
                Search Distributors
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {hasPermission('churned_customers') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonOff color="warning" />
                  <Typography variant="h6">
                    Churned Customers
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Identify customers who haven't purchased in the last 70 days from your selected store. 
                  Analyze their purchase history for re-engagement strategies.
                </Typography>
                
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={handleOpenChurnedDialog}
                  startIcon={<PersonOff />}
                  color="warning"
                >
                  Find Churned Customers
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('customer_reports') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person color="success" />
                  <Typography variant="h6">
                    Customer Refills
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Manage customer refill medications. Add customers to refills program, view and edit their 
                  medicine lists, remove items or entire refill records, and calculate min-max values.
                </Typography>
                
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={handleOpenRefillsManagementDialog}
                  startIcon={<Person />}
                  color="success"
                >
                  Customer Refills Management
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('item_min_max') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Settings color="info" />
                  <Typography variant="h6">
                    Min-Max Management
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  View and manage minimum and maximum stock levels for all items in your store.
                  Set inventory thresholds and control item activation status.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleOpenMinMaxDialog}
                  startIcon={<Settings />}
                  color="info"
                >
                  Manage Min-Max
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Description sx={{ color: '#d32f2f' }} />
                  <Typography variant="h6">
                    Pharmacist Audit
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Generate pharmacist audit PDF reports from CSV data. Upload sales data,
                  automatically select quality sample bills, and generate professional audit reports.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleOpenPharmacistAuditDialog}
                  startIcon={<Description />}
                  sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
                >
                  Generate Audit Report
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccountBalance sx={{ color: '#1976d2' }} />
                  <Typography variant="h6">
                    Purchase Reconciliation
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Reconcile bank statement debits against distributor purchases for a branch
                  and date. Matches payments to purchases per distributor and flags unmatched
                  or mismatched records for manual review.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setReconciliationDialogOpen(true)}
                  startIcon={<AccountBalance />}
                >
                  Open Reconciliation
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccountBalanceWallet sx={{ color: '#2e7d32' }} />
                  <Typography variant="h6">
                    Purchase Payments
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Sync purchases from evitalrx, view all pending (Due) bills grouped by
                  distributor, and mark bills as paid once the account team makes the transfer.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setPurchasePaymentsOpen(true)}
                  startIcon={<AccountBalanceWallet />}
                  sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                >
                  Manage Payments
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MonetizationOn sx={{ color: '#f57c00' }} />
                  <Typography variant="h6">
                    Sales from evitalrx
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Sync retail sales bills from evitalrx, view daily totals broken down by
                  payment method (Cash / UPI / Card), and browse individual bills by staff
                  member or date range.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setSalesOpen(true)}
                  startIcon={<MonetizationOn />}
                  sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#e65100' } }}
                >
                  View Sales
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingDown sx={{ color: '#c62828' }} />
                  <Typography variant="h6">
                    Non-Moving Items
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Find items purchased in a date range that haven't sold since — cross-checked
                  against the open order-request backlog, so you can spot stock that's both
                  dead and possibly still owed to a customer.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setNonMovingItemsOpen(true)}
                  startIcon={<TrendingDown />}
                  sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#8e0000' } }}
                >
                  View Report
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Savings sx={{ color: '#2e7d32' }} />
                  <Typography variant="h6">
                    Discount Optimizer
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Recommends a per-item Max Disc.(%) cap to lift margin without touching fast-moving
                  or broad-audience products, and without moving any big bill more than ~1%. Tune the
                  knobs, watch the projected margin lift, and export the list to apply in evitalRX.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setDiscountOptimizerOpen(true)}
                  startIcon={<Savings />}
                  sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                >
                  Optimize Discounts
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('customer_reports') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EventRepeat sx={{ color: '#6a1b9a' }} />
                  <Typography variant="h6">
                    Monthly Customer Refills
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Finds the customers who come back every month for the same medicines, sorted by the
                  part of the month they usually buy. Shows each one's repeat basket with quantity,
                  MRP, discount, the estimated bill for this month and whether it is in stock — and
                  marks off who has already refilled.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setMonthlyRefillsOpen(true)}
                  startIcon={<EventRepeat />}
                  sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}
                >
                  View Monthly Refills
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('utilities') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Receipt sx={{ color: '#7b1fa2' }} />
                  <Typography variant="h6">
                    Purchase Returns
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Sync purchase return entries from evitalrx. View Draft, Due, and Settled
                  returns per distributor with amounts and outstanding dues.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setPurchaseReturnsOpen(true)}
                  startIcon={<Receipt />}
                  sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#4a148c' } }}
                >
                  View Returns
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {hasPermission('stock_transfers') && (
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SwapHoriz sx={{ color: '#00695c' }} />
                  <Typography variant="h6">
                    Stock Transfers
                  </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Find slow-moving stock at one store that's fast-moving with low stock at
                  another, and create a transfer instead of a fresh purchase order.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setStockTransfersOpen(true)}
                  startIcon={<SwapHoriz />}
                  sx={{ bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' } }}
                >
                  Open Stock Transfers
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Medicine Search Dialog */}
      <Dialog 
        open={medicineDialogOpen} 
        onClose={handleCloseMedicineDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospital />
            Indian Medicine Lookup
          </Box>
          <IconButton onClick={handleCloseMedicineDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by medicine name, manufacturer, or composition"
              label="Medicine Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: loading && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            <Button 
              variant="contained" 
              onClick={handleSearchMedicines}
              disabled={loading || !searchQuery.trim()}
              startIcon={<Search />}
            >
              Search
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Medicine Name</TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((medicine: IndianMedicine) => (
                    <TableRow key={medicine.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {medicine.name}
                        </Typography>
                        {medicine.pack_size_label && (
                          <Typography variant="caption" color="textSecondary">
                            {medicine.pack_size_label}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{medicine.manufacturer_name || 'N/A'}</TableCell>
                      <TableCell>{medicine.type || 'N/A'}</TableCell>
                      <TableCell>{formatPrice(medicine.price)}</TableCell>
                      <TableCell>
                        {getDiscontinuedChip(medicine.Is_discontinued)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewDetails(medicine)}
                            color="primary"
                          >
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseMedicineDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Medicine Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospital />
            Medicine Details
          </Box>
          <IconButton onClick={handleCloseDetailDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          {selectedMedicine && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3">
                      {selectedMedicine.name}
                    </Typography>
                    {getDiscontinuedChip(selectedMedicine.Is_discontinued)}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <MonetizationOn sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Pricing & Basic Info
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2"><strong>Price:</strong> {formatPrice(selectedMedicine.price)}</Typography>
                    <Typography variant="body2"><strong>Manufacturer:</strong> {selectedMedicine.manufacturer_name || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Type:</strong> {selectedMedicine.type || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Pack Size:</strong> {selectedMedicine.pack_size_label || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <Category sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Composition
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {selectedMedicine.short_composition1 && (
                      <Typography variant="body2"><strong>Primary:</strong> {selectedMedicine.short_composition1}</Typography>
                    )}
                    {selectedMedicine.short_composition2 && (
                      <Typography variant="body2"><strong>Secondary:</strong> {selectedMedicine.short_composition2}</Typography>
                    )}
                    {selectedMedicine.salt_composition && (
                      <Typography variant="body2"><strong>Salt Composition:</strong> {selectedMedicine.salt_composition}</Typography>
                    )}
                  </Box>
                </Grid>
                
                {selectedMedicine.medicine_desc && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {selectedMedicine.medicine_desc}
                    </Typography>
                  </Grid>
                )}
                
                {selectedMedicine.side_effects && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      <Warning sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Side Effects
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {selectedMedicine.side_effects}
                    </Typography>
                  </Grid>
                )}
                
                {selectedMedicine.drug_interactions && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="error.main" gutterBottom>
                      <Warning sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Drug Interactions
                    </Typography>
                    <Typography variant="body2">
                      {selectedMedicine.drug_interactions}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Distributor Lookup Dialog */}
      <Dialog 
        open={distributorDialogOpen} 
        onClose={handleCloseDistributorDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping />
            Distributor Lookup
          </Box>
          <IconButton onClick={handleCloseDistributorDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by medicine name or manufacturer name"
              label="Medicine/Manufacturer Search"
              value={distributorSearchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDistributorSearchQuery(e.target.value)}
              onKeyPress={handleDistributorKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: distributorLoading && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            <Button 
              variant="contained" 
              onClick={handleSearchDistributors}
              disabled={distributorLoading || !distributorSearchQuery.trim()}
              startIcon={<Search />}
              color="secondary"
            >
              Search Distributors
            </Button>
          </Box>

          {distributorError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {distributorError}
            </Alert>
          )}

          {distributorResults.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Distributor Name</TableCell>
                    <TableCell>Last Purchase Date</TableCell>
                    <TableCell>Total Purchases</TableCell>
                    <TableCell>Last Bill No</TableCell>
                    <TableCell>Medicine/Manufacturer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {distributorResults.map((distributor: DistributorLookup, index: number) => (
                    <TableRow key={`${distributor.distributor_id}-${index}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business fontSize="small" color="secondary" />
                          <Typography variant="body2" fontWeight="medium">
                            {distributor.distributor_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {new Date(distributor.last_purchase_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={distributor.total_purchases} 
                          size="small" 
                          color="info" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {distributor.last_bill_no && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Receipt fontSize="small" color="action" />
                            <Typography variant="body2">
                              {distributor.last_bill_no}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {distributor.medicine_name && (
                            <span><strong>Medicine:</strong> {distributor.medicine_name}<br /></span>
                          )}
                          {distributor.manufacturer_name && (
                            <span><strong>Manufacturer:</strong> {distributor.manufacturer_name}</span>
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDistributorDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Churned Customers Dialog */}
      <Dialog 
        open={churnedDialogOpen} 
        onClose={handleCloseChurnedDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonOff />
            Churned Customers Analysis
          </Box>
          <IconButton onClick={handleCloseChurnedDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Customers who haven't purchased in the last 70 days from your selected store. 
              Use this data to create re-engagement campaigns.
            </Typography>
            
            {churnedLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading churned customers...</Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={() => handleLoadChurnedCustomers(churnedPage)}
                disabled={churnedLoading}
                startIcon={<PersonOff />}
                color="warning"
              >
                Refresh Data
              </Button>
              
              <Button 
                variant="contained" 
                onClick={handleDownloadChurnedCsv}
                disabled={downloadingCsv || churnedResults.length === 0}
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                color="success"
              >
                {downloadingCsv ? 'Downloading...' : 'Download CSV'}
              </Button>
            </Box>
          </Box>

          {churnedError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {churnedError}
            </Alert>
          )}

          {churnedResults.length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Found {churnedPagination.total} churned customers
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Showing {churnedPagination.offset + 1}-{churnedPagination.offset + churnedResults.length} of {churnedPagination.total}
                </Typography>
              </Box>
              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Details</TableCell>
                      <TableCell>Last Purchase</TableCell>
                      <TableCell>Days Inactive</TableCell>
                      <TableCell>Total Bills</TableCell>
                      <TableCell>Total Sales Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {churnedResults.map((customer: ChurnedCustomer) => (
                      <TableRow key={customer.customer_id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {customer.customer_name || 'Unknown Customer'}
                            </Typography>
                            {customer.customer_number && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Phone fontSize="small" color="action" />
                                <Typography variant="caption" color="textSecondary">
                                  {customer.customer_number}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="warning" />
                            <Typography variant="body2">
                              {new Date(customer.last_purchase_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${customer.days_since_last_purchase} days`}
                            size="small" 
                            color="warning" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.total_bills} 
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney fontSize="small" color="success" />
                            <Typography variant="body2" fontWeight="medium" color="success.main">
                              ₹{customer.total_sales_amount.toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination Controls */}
              {churnedPagination.total > churnedPagination.limit && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(churnedPagination.total / churnedPagination.limit)}
                    page={churnedPage}
                    onChange={handleChurnedPageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseChurnedDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Customer Refills Management Dialog */}
      <Dialog 
        open={refillsManagementDialogOpen} 
        onClose={handleCloseRefillsManagementDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person />
            Customer Refills Management
          </Box>
          <IconButton onClick={handleCloseRefillsManagementDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <CustomerRefillsManagement />
        </DialogContent>
      </Dialog>

      {/* Min-Max Management Dialog */}
      <Dialog
        open={minMaxDialogOpen}
        onClose={handleCloseMinMaxDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings />
            Min-Max Management
          </Box>
          <IconButton onClick={handleCloseMinMaxDialog}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <MinMaxManagement selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Pharmacist Audit Dialog */}
      <Dialog
        open={pharmacistAuditDialogOpen}
        onClose={handleClosePharmacistAuditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: '#d32f2f' }} />
            Pharmacist Audit Report Generator
          </Box>
          <IconButton onClick={handleClosePharmacistAuditDialog}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <PharmacistAudit />
        </DialogContent>
      </Dialog>

      {/* Purchase Reconciliation Dialog */}
      <Dialog
        open={reconciliationDialogOpen}
        onClose={() => setReconciliationDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance sx={{ color: '#1976d2' }} />
            Purchase Reconciliation
          </Box>
          <IconButton onClick={() => setReconciliationDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <PurchaseReconciliation selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Purchase Payments Dialog */}
      <Dialog
        open={purchasePaymentsOpen}
        onClose={() => setPurchasePaymentsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWallet sx={{ color: '#2e7d32' }} />
            Purchase Payments
          </Box>
          <IconButton onClick={() => setPurchasePaymentsOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <PurchasePayments selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Sales from evitalrx Dialog */}
      <Dialog
        open={salesOpen}
        onClose={() => setSalesOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOn sx={{ color: '#f57c00' }} />
            Sales from evitalrx
          </Box>
          <IconButton onClick={() => setSalesOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <EvitalrxSales selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Non-Moving Items Dialog */}
      <Dialog
        open={nonMovingItemsOpen}
        onClose={() => setNonMovingItemsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingDown sx={{ color: '#c62828' }} />
            Non-Moving Items
          </Box>
          <IconButton onClick={() => setNonMovingItemsOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <NonMovingItems selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Discount Optimizer Dialog */}
      <Dialog
        open={discountOptimizerOpen}
        onClose={() => setDiscountOptimizerOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Savings sx={{ color: '#2e7d32' }} />
            Discount Optimizer
          </Box>
          <IconButton onClick={() => setDiscountOptimizerOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DiscountOptimizer selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Monthly Customer Refills Dialog */}
      <Dialog
        open={monthlyRefillsOpen}
        onClose={() => setMonthlyRefillsOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventRepeat sx={{ color: '#6a1b9a' }} />
            Monthly Customer Refills
          </Box>
          <IconButton onClick={() => setMonthlyRefillsOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <MonthlyRefills selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Purchase Returns Dialog */}
      <Dialog
        open={purchaseReturnsOpen}
        onClose={() => setPurchaseReturnsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt sx={{ color: '#7b1fa2' }} />
            Purchase Returns
          </Box>
          <IconButton onClick={() => setPurchaseReturnsOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <EvitalrxPurchaseReturns selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>

      {/* Stock Transfers Dialog */}
      <Dialog
        open={stockTransfersOpen}
        onClose={() => setStockTransfersOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHoriz sx={{ color: '#00695c' }} />
            Stock Transfers
          </Box>
          <IconButton onClick={() => setStockTransfersOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <StockTransfers selectedStore={selectedStore} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Utilities;
